import React, { useState, useEffect } from 'react'
import { Link, useParams } from 'react-router-dom'
import styled from 'styled-components'
import SEO from '../components/SEO'
import ShopChat from '../components/ShopChat'
import { ShopPage, ShopTitle, ShopSubtitle, Section, SectionHeading, Crumbs, Photo, Rating } from '../components/ShopChat/shopcommon'
import { fetchWrapper } from '../utils/apiHandlers'
import theme from '../theme'

const ShopBusiness: React.FC = () => {
  const { slug } = useParams<{ slug: string }>()
  const [business, setbusiness] = useState<any>(null)
  const [notfound, setnotfound] = useState(false)
  const [active, setactive] = useState(0)
  const [showhours, setshowhours] = useState(false)

  useEffect(() => {
    setbusiness(null)
    setnotfound(false)
    setactive(0)
    fetchWrapper.get(`/api/directory/business/${encodeURIComponent(slug)}`)
      .then((r: any) => (r.status === 200 ? setbusiness(r.business) : setnotfound(true)))
      .catch(() => setnotfound(true))
  }, [slug])

  if (notfound) {
    return (
      <ShopPage>
        <ShopTitle style={{ marginTop: 40 }}>Business not found</ShopTitle>
        <ShopSubtitle>This listing may have been removed. <Link to="/ask">Back to Ask Amigo</Link>.</ShopSubtitle>
      </ShopPage>
    )
  }
  if (!business) return <ShopPage />

  const photos: string[] = business.images && business.images.length ? business.images : business.image ? [business.image] : []
  const address = [business.address, business.city, business.state, business.zip].filter(Boolean).join(', ')
  const jsonLd: any = { "@context": "https://schema.org", "@type": "LocalBusiness", "name": business.name, "url": business.websiteurl }
  if (business.description) jsonLd.description = business.description
  if (business.phone) jsonLd.telephone = business.phone
  if (photos.length) jsonLd.image = photos.map((p) => (p.startsWith('http') ? p : `https://miamigo.ai${p}`))
  if (typeof business.rating === 'number') jsonLd.aggregateRating = { "@type": "AggregateRating", "ratingValue": business.rating, "reviewCount": business.ratingcount || 1 }
  if (address) {
    jsonLd.address = { "@type": "PostalAddress", "streetAddress": business.address, "addressLocality": business.city, "addressRegion": business.state, "postalCode": business.zip }
  }
  if (typeof business.lat === 'number' && typeof business.lon === 'number') {
    jsonLd.geo = { "@type": "GeoCoordinates", "latitude": business.lat, "longitude": business.lon }
  }

  return (
    <ShopPage>
      <SEO
        title={`${business.name}${business.city ? ` - ${business.city}` : ''} | ${business.categorylabel} | MiAmigo.AI`}
        description={(business.description || `${business.name} is a local business${business.city ? ` in ${business.city}` : ''}. Ask our AI assistant about what they offer.`).slice(0, 300)}
        path={`/places/${business.category}/${business.slug}`}
        jsonLd={jsonLd}
      />
      <Crumbs>
        <Link to="/ask">Ask Amigo</Link> / <Link to={`/places/${business.category}`}>{business.categorylabel}</Link> / {business.name}
      </Crumbs>

      <Section>
        <Top>
          <Gallery>
            <Main><Photo src={photos[active]} name={business.name} height={380} /></Main>
            {photos.length > 1 && (
              <Thumbs>
                {photos.map((p, i) => (
                  <Thumb key={p} $active={i === active} onClick={() => setactive(i)}>
                    <Photo src={p} name={business.name} height={62} />
                  </Thumb>
                ))}
              </Thumbs>
            )}
            {business.imageattribution && <Credit>Photos: {business.imageattribution} via Google Maps</Credit>}
          </Gallery>

          <Info>
            <ShopTitle style={{ textAlign: 'left', fontSize: 30 }}>{business.name}</ShopTitle>
            <Sub>{business.categorylabel}{business.city ? ` in ${business.city}` : ''}</Sub>
            <Rating rating={business.rating} count={business.ratingcount} />
            {business.description && <Desc>{business.description}</Desc>}
            {address && <Row><b>Address</b> {address}</Row>}
            {business.phone && <Row><b>Phone</b> <a href={`tel:${business.phone}`}>{business.phone}</a></Row>}
            {business.hours && business.hours.length > 0 && (
              <Row>
                <b>Hours</b>{' '}
                <ToggleLink onClick={() => setshowhours(!showhours)}>{showhours ? 'Hide' : 'Show weekly hours'}</ToggleLink>
                {showhours && <Hours>{business.hours.map((h: string) => <li key={h}>{h}</li>)}</Hours>}
              </Row>
            )}
            <Actions>
              {business.websiteurl && <Primary href={business.websiteurl} target="_blank" rel="noopener noreferrer">Visit website ↗</Primary>}
              {business.mapsurl && <Secondary href={business.mapsurl} target="_blank" rel="noopener noreferrer">Directions</Secondary>}
            </Actions>
          </Info>
        </Top>
      </Section>

      <Section style={{ maxWidth: 680 }}>
        <SectionHeading>Ask about {business.name}</SectionHeading>
        <ShopChat slug={business.slug} placeholder="Ask about the menu, prices, hours..." greeting={`Hi! Ask me anything about ${business.name}.`} />
      </Section>

      <Claim>
        Own this business? <Link to="/register">Get an AI chatbot like this on your own website</Link>.
      </Claim>
    </ShopPage>
  )
}

const Top = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.15fr) minmax(0, 1fr);
  gap: 28px;
  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
`
const Gallery = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`
const Main = styled.div`
  border-radius: ${theme.radius.lg};
  overflow: hidden;
  box-shadow: ${theme.shadow.raised};
`
const Thumbs = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
`
const Thumb = styled.div<{ $active: boolean }>`
  cursor: pointer;
  border-radius: ${theme.radius.sm};
  overflow: hidden;
  border: 2px solid ${(p) => (p.$active ? theme.colors.primary : 'transparent')};
`
const Credit = styled.div`
  font-size: 11px;
  color: ${theme.colors.textMuted};
`
const Info = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  font-size: 14px;
  line-height: 1.6;
`
const Sub = styled.div`
  color: ${theme.colors.textMuted};
  font-size: 15px;
`
const Desc = styled.p`
  margin: 4px 0;
`
const Row = styled.div`
  b {
    display: inline-block;
    min-width: 72px;
    color: ${theme.colors.textMuted};
    font-weight: 600;
  }
  a {
    color: ${theme.colors.primary};
  }
`
const ToggleLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: ${theme.colors.primary};
  text-decoration: underline;
  font-size: 14px;
`
const Hours = styled.ul`
  margin: 6px 0 0 72px;
  padding: 0;
  list-style: none;
  font-size: 13px;
`
const Actions = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 8px;
  flex-wrap: wrap;
`
const Primary = styled.a`
  background: ${theme.colors.primary};
  color: #fff;
  padding: 11px 22px;
  border-radius: ${theme.radius.md};
  font-weight: 700;
  text-decoration: none;
`
const Secondary = styled.a`
  border: 1px solid ${theme.colors.primary};
  color: ${theme.colors.primary};
  padding: 10px 20px;
  border-radius: ${theme.radius.md};
  font-weight: 700;
  text-decoration: none;
`
const Claim = styled.div`
  font-size: 13px;
  color: ${theme.colors.textMuted};
  a {
    color: ${theme.colors.primary};
    font-weight: 600;
  }
`

export default ShopBusiness
